import type React from 'react';
import { useSelector } from 'react-redux';

import { Alert, CloseButton, TextButton } from '@zillow/constellation-10';
import { useAcceptRentAmountChangeWebApiHandler_acceptRentAmountChange } from '@zg-rentals/ts-rent-payment-client-react-query';

import { closeNotification } from '@common/actions/NotificationActions';
import { showRentAmountChange } from '@renter-hub/actions/onboarding';
import { MANUAL_FREQUENCY } from '@renter-hub/constants/paymentConstants';
import StringConstants from '@common/constants/StringConstants';
import PlaidButton from '@common/components/PlaidButton';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';
import { dismissMarkedPayments } from './markedPaidBannerDismissal';

type ReviewRentAmountChangeProps = {
  landlordFirstName: string;
};

type RentAmountChangeExpiredProps = {
  landlordFirstName: string;
  landlordEmail: string;
};

type VerifySingleBankAccountBannerProps = {
  paymenMethodStatus: string;
  accountLast4: string;
  plaidEnv: string;
  plaidLinkToken?: string;
  plaidLinkTokenExpiration?: number;
  getPlaidLinkToken: () => Promise<any>;
  onSuccess: (plaidToken: any) => Promise<void>;
  onExit: (error: any, metadata: any) => void;
};

type VerifyMultipleBankAccountsBannerProps = {
  onManagePaymentMethodsClick: () => void;
};

type MarkedPaidPayment = {
  key: string;
  label: string;
  amount: string;
};

type PaymentMarkedPaidByLandlordProps = {
  landlordFirstName: string;
  payments: Array<MarkedPaidPayment>;
};

type CommonProps = {
  dispatch: (...args: Array<any>) => any;
};

const PaymentMethodDeleted = ({ paymentMethodDeleted, onClose }) => (
  <Alert tone="success" onClose={onClose} closeButton={<CloseButton />}>
    {`${paymentMethodDeleted} was successfully deleted`}
  </Alert>
);

const NewPaymentMethodAdded = ({ bankName, needsVerification = false, onClose }) => {
  return (
    <Alert tone="success" onClose={onClose} closeButton={<CloseButton />}>
      {`${bankName} successfully added. ${
        needsVerification
          ? 'You will be able to use your account as soon as it is verified.'
          : 'You can now use this payment method.'
      }`}
    </Alert>
  );
};

const PaymentScheduleSuccessDetailed = ({ typeofPayment = MANUAL_FREQUENCY, dateOfTheMonth, date, onClose }) => (
  <Alert tone="success" onClose={onClose} closeButton={<CloseButton />}>
    {typeofPayment === MANUAL_FREQUENCY
      ? `Your one time payment has been scheduled for ${date}`
      : `Your recurring payment has been scheduled for the ${dateOfTheMonth} of the month`}
  </Alert>
);

const MicroDepositLimitReached = ({ dispatch, onClose }: CommonProps & { onClose?: () => void }): React.ReactNode => (
  <Alert
    tone="warning"
    onClose={onClose}
    closeButton={<CloseButton />}
    actionButton={
      <TextButton
        onClick={() =>
          Promise.resolve()
            .then(() => dispatch(closeNotification()))
            .then(() => {
              if (typeof window !== 'undefined') {
                window.location.href = StringConstants.LINKS.FAQ_PAGE.zillow;
              }
            })
        }
      >
        Contact support
      </TextButton>
    }
  >
    You have exceeded the allowed number of attempts
  </Alert>
);

const ReviewRentAmountChange = ({
  dispatch,
  landlordFirstName = 'Your landlord',
  onClose,
}: ReviewRentAmountChangeProps & CommonProps & { onClose?: () => void }): React.ReactNode => (
  <Alert
    tone="warning"
    onClose={onClose}
    closeButton={<CloseButton />}
    actionButton={
      <TextButton
        onClick={() =>
          Promise.resolve()
            .then(() => dispatch(closeNotification()))
            .then(() => dispatch(showRentAmountChange()))
        }
      >
        Review and accept
      </TextButton>
    }
  >
    {landlordFirstName} has updated your rent amount
  </Alert>
);

const RentAmountChangeExpired = ({
  dispatch,
  landlordEmail,
  landlordFirstName = 'your landlord',
}: RentAmountChangeExpiredProps & CommonProps): React.ReactNode => {
  const { mutateAsync: requestAcceptRentAmountChange } =
    useAcceptRentAmountChangeWebApiHandler_acceptRentAmountChange();

  const handleClose = async () => {
    await dispatch(closeNotification());
    await requestAcceptRentAmountChange({
      postData: { acceptExpiration: true },
    });
  };

  return (
    <Alert
      tone="warning"
      closeButton={<CloseButton />}
      onClose={handleClose}
      actionButton={
        <TextButton asChild>
          <a href={`mailto:${landlordEmail}`}>Get in touch with {landlordFirstName}</a>
        </TextButton>
      }
    >
      An update to your rent amount has expired
    </Alert>
  );
};

const VerifySingleBankAccountBanner = ({
  paymenMethodStatus,
  accountLast4,
  plaidEnv,
  plaidLinkToken,
  plaidLinkTokenExpiration,
  getPlaidLinkToken,
  onSuccess,
  onExit,
  onClose,
}: VerifySingleBankAccountBannerProps & { onClose?: () => void }): React.ReactNode => {
  const isPlaidRevoked = paymenMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED;
  const userZuid = useSelector((state: any) => state.user?.userToken);
  return (
    <Alert
      tone={isPlaidRevoked ? 'warning' : 'info'}
      onClose={onClose}
      closeButton={<CloseButton />}
      actionButton={
        <PlaidButton
          plaidEnv={plaidEnv}
          plaidLinkToken={plaidLinkToken}
          plaidLinkTokenExpiration={plaidLinkTokenExpiration}
          getPlaidLinkToken={getPlaidLinkToken}
          label={isPlaidRevoked ? 'Reconnect with Plaid' : 'Verify bank account'}
          onSuccess={onSuccess}
          onExit={onExit}
          dataTest="verify-bank-account-plaid-button"
          useConstellationButton={true}
          variant="text"
          size="sm"
          full={false}
          userZuid={userZuid}
        />
      }
    >
      {isPlaidRevoked
        ? `We lost the verified connection to your bank account ending in ${accountLast4}`
        : `Complete verification for your bank account ending in ${accountLast4}`}
    </Alert>
  );
};

const VerifyMultipleBankAccountsBanner = ({
  onManagePaymentMethodsClick,
  onClose,
}: VerifyMultipleBankAccountsBannerProps & { onClose?: () => void }): React.ReactNode => (
  <Alert
    tone="info"
    onClose={onClose}
    closeButton={<CloseButton />}
    actionButton={<TextButton onClick={onManagePaymentMethodsClick}>Manage payment methods</TextButton>}
  >
    Multiple bank accounts need attention
  </Alert>
);

const PaymentMarkedPaidByLandlord = ({
  onClose,
  landlordFirstName = 'Your landlord',
  payments,
}: PaymentMarkedPaidByLandlordProps & { onClose?: () => void }): React.ReactNode => (
  <Alert
    tone="success"
    onClose={() => {
      dismissMarkedPayments(payments.map((payment) => payment.key));
      onClose?.();
    }}
    closeButton={<CloseButton />}
  >
    <strong>
      {payments.length === 1
        ? `${landlordFirstName} marked your ${payments[0].label} of ${payments[0].amount} as paid`
        : `${landlordFirstName} marked ${payments.length} payments as paid: ${payments
            .map((payment) => `${payment.label} (${payment.amount})`)
            .join(', ')}`}
    </strong>
    <div>Your past-due status has been cleared. No action needed on your end.</div>
  </Alert>
);

export {
  MicroDepositLimitReached,
  ReviewRentAmountChange,
  RentAmountChangeExpired,
  PaymentMethodDeleted,
  NewPaymentMethodAdded,
  PaymentScheduleSuccessDetailed,
  VerifySingleBankAccountBanner,
  VerifyMultipleBankAccountsBanner,
  PaymentMarkedPaidByLandlord,
};
